import React, { useState } from 'react';
import { ui } from '../../../config/uiSizes';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../Toast/ToastProvider';
import { logger } from '../../../utils/logger';
import updateSnapshotRetention, {
    MIN_RETENTION_LIMIT,
    MAX_RETENTION_LIMIT
} from '../../../services/Migration/updateSnapshotRetentionService';
import { validateRetentionLimit, retentionReductionDropsHistory } from '../../../utils/retention';

interface RetentionEditorProps {
    migrationId: string;
    currentLimit: number;
    historyCount: number;
    /** Notifies the parent of a successful update so it can refresh derived state. */
    onUpdated?: (newLimit: number, historyCount: number) => void;
}

/**
 * Inline editor for a recurring snapshot's retention limit — how many captured
 * snapshots are kept before the oldest are trimmed. Wraps the backend
 * PATCH /api/migrations/{id}/retention endpoint (previously reachable only via API).
 * Warns before a reduction that would immediately drop existing history.
 */
const RetentionEditor: React.FC<RetentionEditorProps> = ({
    migrationId,
    currentLimit,
    historyCount,
    onUpdated
}) => {
    const { accessToken } = useAuth();
    const { showToast } = useToast();
    const [value, setValue] = useState<string>(String(currentLimit));
    const [saving, setSaving] = useState(false);

    const parsed = Number(value);
    const validation = validateRetentionLimit(parsed);
    const isDirty = value.trim() !== '' && parsed !== currentLimit;
    const dropsHistory = validation.valid && retentionReductionDropsHistory(parsed, historyCount);
    const dropCount = dropsHistory ? historyCount - parsed : 0;

    const handleSave = async () => {
        if (!accessToken || !validation.valid || !isDirty) return;
        setSaving(true);
        try {
            const result = await updateSnapshotRetention(accessToken, migrationId, parsed);
            showToast(`Retention updated to ${result.retentionLimit} snapshots.`, 'success');
            onUpdated?.(result.retentionLimit, result.historyCount);
        } catch (error) {
            logger.error('Retention update failed in editor', {
                error: error instanceof Error ? error.message : String(error),
                migrationId
            });
            showToast('Failed to update retention limit. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className={`${ui.page.sectionPadding} bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700`}
        >
            <div className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 ${ui.mb.sm}`}>
                Retention
            </div>
            <div className={`${ui.text.xs} text-gray-500 dark:text-gray-400 ${ui.mb.title}`}>
                Number of snapshots to keep ({MIN_RETENTION_LIMIT}–{MAX_RETENTION_LIMIT}). Older snapshots are
                removed automatically once the limit is exceeded.
            </div>

            <div className={`flex items-center flex-wrap ${ui.gap.sm}`}>
                <input
                    type="number"
                    min={MIN_RETENTION_LIMIT}
                    max={MAX_RETENTION_LIMIT}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={saving}
                    className={`${ui.input.sm} w-24 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-60`}
                    aria-label="Snapshot retention limit"
                />
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !isDirty || !validation.valid}
                    className={`${ui.button.md} rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-semibold shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${ui.gap.xs}`}
                >
                    {saving ? (
                        <>
                            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" aria-hidden="true">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            <span>Saving…</span>
                        </>
                    ) : (
                        <span>Save</span>
                    )}
                </button>
            </div>

            {!validation.valid && value.trim() !== '' && (
                <div className={`${ui.text.xs} text-red-600 dark:text-red-400 mt-2`}>{validation.error}</div>
            )}
            {dropsHistory && (
                <div className={`${ui.text.xs} text-amber-600 dark:text-amber-400 mt-2`}>
                    Lowering to {parsed} will remove the {dropCount} oldest snapshot{dropCount === 1 ? '' : 's'}{' '}
                    from history immediately.
                </div>
            )}
        </div>
    );
};

export default RetentionEditor;
